import { SearchOutlined } from '@ant-design/icons';
import { Button, Space } from 'antd';
import { notification } from '@/components/Notification';
import { message } from '@/components/Message';
import React, { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { POSConfigContext } from '@/components/LayoutPOS';
import { POSBaseInfoContext } from '@/components/LayoutPOS/Synchronizer';
import NumericKeyboard from '@/components/NumericKeyboard';
import PosButton from '@/components/PosButton';
import {
  addGlobalScanListener,
  removeGlobalScanListener,
} from '@/components/QRScanner/GlobalScanner';
import SpToGoodsModal from '@/components/StandardProductCom/Modal';
import { Card } from '@/models/card';
import { Category } from '@/models/category';
import { DefaultServiceDuration } from '@/models/common';
import { isServiceSKUWrapper } from '@/models/order';
import { SKUSpecification, SKUWrapper } from '@/models/sku';
import { playSound } from '@/utils/sound';
import CardList from './cardList';
import SpecificationModal from './SpecificationModal';
import SpuList from './spuList';
import styles from './style.less';
import Tab from './Tab';

interface Props {
  cardList: Card[];
  categoryList: Category[];
  onAdd: (specification: SKUSpecification) => void;
  onAddCard?: (card: Card) => void;
  actions: React.ReactNode;
  previewMode?: boolean;
  // scannedGoodsSn: string;
}

const Index: React.FC<Props> = ({
  previewMode = false,
  onAdd,
  onAddCard = () => {},
  actions,
  categoryList,
  cardList,
}: Props) => {
  const {
    config: { uiConfig },
  } = useContext(POSConfigContext);
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const [renderType, setRenderType] = useState<'spu' | 'card' | 'search'>();
  const [selectedTabKey, setSelectedTabKey] = useState(null);
  const [skuWrapperList, setSkuWrapperList] = useState<SKUWrapper[]>([]);
  const {
    getSkuInCategory,
    getSkuByGoodsSn,
    getSkuByIds,
    addOrUpdateSkuWrapper,
    searchGoods,
  } = useContext(POSBaseInfoContext);
  const [spToGoodsModalBarcode, setSpToGoodsModalBarcode] = useState<string>();

  const [searchPopOpen, setSearchPopOpen] = useState(false);

  const [activeSkuWrapper, setActiveSkuWrapper] = useState<SKUWrapper>(null);

  // 商品扫码监听
  useEffect(() => {
    const key = addGlobalScanListener('goods', ({ code: sn }) => {
      const skuWrapper = getSkuByGoodsSn(sn);
      if (!skuWrapper) {
        if (/\d{13}/.test(sn)) {
          notification.info({
            duration: null,
            message: `商品录入`,
            description: (
              <>
                <div>检测到您扫描了未入库的商品</div>
                <div>是否尝试查询中央商品库信息并添加该商品?</div>
              </>
            ),
            key,
            actions: (
              <Space>
                <Button
                  size={'large'}
                  onClick={() => {
                    notification.destroy(key);
                  }}>
                  忽略
                </Button>
                <Button
                  size={'large'}
                  type={'primary'}
                  onClick={() => {
                    setSpToGoodsModalBarcode(sn);
                    notification.destroy(key);
                  }}>
                  查询商品信息并添加
                </Button>
              </Space>
            ),
          });
          return;
        }
        message.error('没有此商品');
        playSound('error');
      } else {
        setActiveSkuWrapper(skuWrapper);
      }
    });
    return () => {
      removeGlobalScanListener(key);
    };
  }, [getSkuByGoodsSn, setSpToGoodsModalBarcode]);

  // 初始: 选中第一个tab
  useEffect(() => {
    if (categoryList && categoryList.length) {
      if (selectedTabKey === null) {
        setRenderType('spu');
        setSelectedTabKey(`${categoryList[0].id}`);
      }
    }
  }, [categoryList, selectedTabKey]);

  // tab 选中
  useEffect(() => {
    if (selectedTabKey === 'card') {
      setRenderType('card');
    } else if (selectedTabKey === 'search') {
      setRenderType('search');
      setSkuWrapperList([]);
      setSearchPopOpen(true);
    } else if (selectedTabKey) {
      setRenderType('spu');
      setSkuWrapperList(getSkuInCategory(selectedTabKey));
    }
  }, [selectedTabKey, getSkuInCategory]);

  
  const onSpuItemClick = useCallback(
    (skuWrapper: SKUWrapper) => {
      setActiveSkuWrapper(skuWrapper);
    },
    [setActiveSkuWrapper]
  );

  const isHorizontalMode = uiConfig.coSkuAreaDisplayMode === `horizontal`;

  const containerRef = useRef<HTMLDivElement>();

  return (
    <>
      <SpToGoodsModal
        barcode={spToGoodsModalBarcode}
        open={!!spToGoodsModalBarcode}
        onRequestClose={() => {
          setSpToGoodsModalBarcode(null);
        }}
        onSuccess={skuWrapper => {
          setSpToGoodsModalBarcode(null);
          addOrUpdateSkuWrapper(skuWrapper);
          playSound('scan_di');
        }}
      />
      <div className={`${styles.skuArea} ${isHorizontalMode ? styles.horizontal : ''}`}>
        <SpecificationModal
          onCancel={() => {
            setActiveSkuWrapper(null);
          }}
          onSelect={specification => {
            onAdd(
              Object.assign({}, specification, {
                number: 1,
                goodsId: activeSkuWrapper.goods.id,
                goodsName: activeSkuWrapper.goods.name,
                goodsPicUrl: activeSkuWrapper.goods.picUrl,
                goodsSn: activeSkuWrapper.goods.goodsSn,
                goodsType: activeSkuWrapper.goods.type,
                // 服务时长
                goodsDuration: isServiceSKUWrapper(activeSkuWrapper)
                  ? activeSkuWrapper.goods.duration || DefaultServiceDuration
                  : 0,
              } as SKUSpecification)
            );
            setActiveSkuWrapper(null);
          }}
          specifications={activeSkuWrapper ? activeSkuWrapper.specifications : null}
        />

        <Tab
          cardList={cardList}
          categoryList={categoryList}
          selectedTabKey={selectedTabKey}
          onSelectedTabKeyChange={key => {
            setSelectedTabKey(key);
          }}
        />

        <div className={styles.skuAreaContent} ref={containerRef}>
          {renderType === 'spu' && (
            <SpuList
              scrollTopFlag={selectedTabKey}
              containerRef={containerRef}
              previewMode={previewMode}
              skuWrapperList={skuWrapperList}
              onItemClick={onSpuItemClick}
            />
          )}
          {renderType === 'card' && (
            <CardList
              uiConfig={uiConfig}
              cardList={cardList}
              onItemClick={card => {
                onAddCard(card);
              }}
            />
          )}
          {renderType === 'search' && (
            <>
              <SpuList
                scrollTopFlag={selectedTabKey}
                containerRef={containerRef}
                previewMode={previewMode}
                skuWrapperList={skuWrapperList}
                emptyProps={{
                  description: searchKeyword ? '没有匹配的商品' : '请输入关键词搜索',
                  className: 'empty-large',
                }}
                onItemClick={onSpuItemClick}
              />
            </>
          )}
        </div>
        {!previewMode && (
          <div className={styles.skuAreaFastActions}>
            <NumericKeyboard
              mode={`numberAndAlphabet`}
              open={searchPopOpen}
              onOpenChange={open => {
                setSearchPopOpen(open);
                if (!open) {
                  setSearchKeyword('');
                }
              }}
              placeholder={'搜索: 商品编码/拼音/拼音首字母'}
              placement={'topLeft'}
              trigger={'click'}
              closeOnScanReceived
              onChange={code => {
                code = code as string;
                if (code) {
                  const ids = searchGoods(code, { limit: uiConfig.coGoodsItemsRow }).map(
                    result => result.item.id
                  );
                  setSkuWrapperList(getSkuByIds(ids));
                } else {
                  setSkuWrapperList([]);
                }
                setSearchKeyword(code);
              }}
              onComplete={() => {
                setSearchPopOpen(false);
              }}>
              <PosButton
                type={'primary'}
                onClick={() => {
                  setSearchPopOpen(true);
                  setSelectedTabKey('search');
                }}
                icon={<SearchOutlined />}
              />
            </NumericKeyboard>
            {actions}
          </div>
        )}
      </div>
    </>
  );
};

export default memo(Index);
